import { Empty, FoldablePanel, Note, Panel } from "./Panel";
import { useDecidedSignals, useDecidedSummary } from "../hooks/useDecided";
import type { DecidedSignal, DecidedSource, ShadowShape } from "../api/types";
import {
  atSource,
  ceilingWord,
  depthWord,
  fillWord,
  gateTone,
  gateWord,
  gatesTone,
  gatesWord,
  hitWord,
  ladderTone,
  ladderWord,
  laddersTone,
  laddersWord,
  lead,
  marketWord,
  modelGainWord,
  pct,
  outcomeWord,
  planWord,
  pnlWord,
  provingWord,
  shares,
  sourceHint,
  sourceLabel,
  sourcesOf,
  staleTone,
  staleWord,
  stalesTone,
  stalesWord,
  takerEdge,
  takerPrice,
  takerRoi,
  toneColor,
  tone,
  verdict,
} from "../lib/decided";
import { zTone, zWord } from "../lib/zscore";
import { PnLShape } from "./PnLShape";
import { RateCell, RateHead } from "./RateCell";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import { formatDollars } from "../lib/tape";
import { StrategyLiveControls } from "./StrategyLiveControls";
import { StrategyRowActions } from "./StrategyRowActions";

/**
 * Покупка почти решённого окна. Теневая не вся: по окну пишется до десяти строк
 * (два источника × лесенка цен), и торговать разрешено ровно одной клетке —
 * остальные остаются измерением (ADR-0022).
 *
 * # Зачем панель на экране
 *
 * Перепись контрагентов нашла архетип, механика которого у нас есть целиком: 22
 * кошелька, $120 тыс. за 16 суток, средняя цена входа 0.990, медианный вход за
 * 17 с до конца окна, доля выигранных окон у медианного участника 1.00. Замок
 * исхода отвечает на тот же вопрос раньше, чем стакан его отыгрывает. Беда
 * архетипа арифметическая — маржа в цент, — и подсистема входит дешевле, чтобы
 * померить, достаётся ли по такой цене хоть что-нибудь.
 *
 * # Почему таблица про ТЕЙКЕРА
 *
 * Мейкерская половина замера закончена, и закончена отрицательно: за сутки
 * налили 16 заявок из 1366, счёт по ним — четыре доллара. Это измеренный ответ
 * («очередь в решённом окне до нас не доезжает»), а не нехватка данных, и
 * держать его в главной таблице значило бы отдавать восемь колонок из десяти
 * под уже закрытый вопрос. Мейкер уехал в подпись — целиком, вместе со своим
 * знаменателем: закрытый вопрос не стирают, его перестают показывать первым.
 *
 * Тейкер же входит, и вопрос к нему другой: не «хорошо ли замок отбирает окна»
 * (`won`/`settled` по всем окнам), а «что вышло у того, кто вошёл». Три числа,
 * которых для этого мало по отдельности и достаточно вместе:
 *
 *   - УГАДАНО против ЦЕНЫ ВХОДА. Доля сама по себе не значит ничего: 112 из 118
 *     это кромка при цене 0.84 и её отсутствие при 0.95. Объявляется запасом в
 *     сигмах, а не знаком разности;
 *   - ОБЪЁМ. Секунды форы без объёма не стоят ничего, а ноль упёршихся в стакан
 *     входов означает «потолок НЕ ИЗМЕРЕН», а не «объёма нет»;
 *   - СЧЁТ вместе с ОБОРОТОМ. Сумма без оборота не отличает удачу на $10 тыс. от
 *     кромки на $1.3 тыс.
 *
 * # Чем ограничена трата денег
 *
 * Кнопок на панели не было вовсе, и довод был сильный: у подсистемы не было
 * порта постановки ордера, а кнопка по строке, за которой не может стоять
 * ордер, обещала бы действие, которого не существует. С ADR-0022 порт есть, и
 * кнопки появились ровно там, где за ними стоит ордер: переключатель режима над
 * таблицей (StrategyLiveControls) и «взять / пропустить» по строке ленты
 * (StrategyRowActions).
 *
 * Обещание при этом не ослабло, а сменило природу. Нажатие превращается в ордер
 * только при совпадении ТРЁХ условий: порт передан трейдеру в сборке, потолок
 * окружения (DECIDED_MODE, DECIDED_ROLE) разрешает торговать и ключ оператора
 * разрешает торговать. Действующий режим — строгий МИНИМУМ двух последних,
 * поэтому кнопка умеет только ПОНИЗИТЬ; поднять потолок умеет лишь правка .env,
 * то есть выкат. Сколько это стоит, ограничивают DECIDED_DAILY_CAP_USD и
 * DECIDED_MAX_OPEN_ORDERS, а торгует ОДНА клетка журнала — источник и ступень
 * цены, названные настройками. Остальные строки окна остаются измерением, и
 * читаются они ровно так же, как до вехи.
 */
export function DecidedPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = useDecidedSummary(hours, windowLen);
  const sources = summary.data?.sources ?? [];
  // Порог свежести приезжает СО СВОДКОЙ, а не задан здесь: подпись обязана
  // называть то же число, каким посчитаны колонки, и вторая копия константы
  // разошлась бы с первой молча — подпись осталась бы правдоподобной.
  const freshBookMs = summary.data?.fresh_book_ms ?? 0;
  // Форма счёта приезжает ОТДЕЛЬНЫМ запросом на все журналы разом: границы
  // корзин обязаны быть общими, иначе гистограммы соседних панелей перестанут
  // сравниваться, ради чего они и заводились.
  const shapes = useJournalShapes(hours, "decided", windowLen);

  return (
    <>
      <Panel
        title="Почти решённое"
        subtitle={summary.data ? verdict(sources) : undefined}
      >
        {!summary.data ? (
          <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
        ) : sources.length === 0 ? (
          <Empty>
            окон ещё не отобрано: замок закрывается в последнюю минуту окна, и
            строки появляются оттуда
          </Empty>
        ) : (
          <>
            {/* Управление живым режимом — НАД таблицей, а не на отдельном
                экране: решение о режиме принимается по числам замера, и
                разведи их по разным страницам — оператору пришлось бы помнить
                цифру, переходя по ссылке. */}
            <StrategyLiveControls id="decided" />
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[var(--color-muted)]">
                  <tr>
                    <th className="pb-2 font-normal">цена</th>
                    <th className="pb-2 font-normal">источник</th>
                    <th className="pb-2 text-right font-normal">входов</th>
                    {/* Доля уцелевшего замера — ПЕРЕД результатом, а не в
                        подписи: всё правее считается по свежей половине, и
                        читатель обязан увидеть её размер раньше, чем её
                        числа. «0 из 57» означает, что строка ничего не
                        измерила, и прочерки справа — это ответ, а не пустота. */}
                    <th className="pb-2 text-right font-normal">свежих</th>
                    <th className="pb-2 text-right font-normal">угадано</th>
                    <th className="pb-2 text-right font-normal">цена входа</th>
                    <th className="pb-2 text-right font-normal">кромка</th>
                    <th className="pb-2 text-right font-normal">запас</th>
                    <th className="pb-2 text-right font-normal">взято</th>
                    <th className="pb-2 text-right font-normal">оборот</th>
                    <th className="pb-2 text-right font-normal">счёт</th>
                    <th className="pb-2 text-right font-normal">доход</th>
                    <RateHead />
                    <th className="pb-2 text-right font-normal">форма счёта</th>
                    <th className="pb-2 text-right font-normal">фора</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <SourceRow
                      key={`${s.limit_price}|${s.source}`}
                      source={s}
                      hours={hours}
                      shape={shapeOf(shapes, `${s.limit_price}|${s.source}`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* ВОЗРАСТ СНИМКА СТАКАНА — первым, потому что он решает, значат ли
                числа в таблице хоть что-нибудь, а лесенка ниже — лишь можно ли
                их сравнивать между собой.

                Половин две, и обе вслух, ровно по доводу ворот: спрятав
                протухшую, панель сменила бы одну неправду на другую — «у нас
                всегда было честно», — и вопрос «куда делись прежние плюсы»
                стал бы неотвечаемым. Здесь он отвечен числом. */}
            <Note
              label={
                <>
                  стакан —{" "}
                  <span style={{ color: stalesTone(sources) }}>{stalesWord(sources)}</span>
                </>
              }
            >
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {sources.map((s) => (
                  <li key={`stale|${s.limit_price}|${s.source}`}>
                    <b>
                      протухшие · {s.limit_price} · {sourceLabel(s.source)}
                    </b>{" "}
                    — {staleWord(s)}.
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-[var(--color-muted)]">
                Свежим считается снимок моложе {freshBookMs} мс — по нему и
                посчитаны «угадано», «цена входа», «кромка», «запас», «оборот»,
                «счёт», «доход» и гистограмма. Дальше цена перестаёт описывать
                биржу: теневой счёт РАСТЁТ с возрастом нашего снимка, и на
                секунде-трёх доходность выходит за восемьдесят процентов —
                величина, невозможная физически, потому что покупка по ≤ 0.999 с
                выплатой $1 не даёт и процента. Это уровни, которых на бирже уже
                нет: мы читаем книгу, которую она успела перебрать. Живая
                проверка сошлась с разрезом: девять настоящих исполнений дали
                −$10.47, и все три налива дешевле 0.90 проиграли.
              </p>
            </Note>
            {/* СОПОСТАВИМОСТЬ ЛЕСЕНКИ — самое важное предложение на панели, и
                поэтому оно в ПОДПИСИ фолда, а не внутри него: под клик уехал
                разбор по источникам, а приговор виден со свёрнутого экрана.
                Отбор окна от цены не зависит, поэтому у настоящей лесенки число
                отобранных окон СОВПАДАЕТ у всех ступеней одного источника.
                Расхождение означает, что ступени набраны на разных окнах, то
                есть разница между ними — календарь, а не цена. Ровно так и
                читалась панель до лесенки: «0.95: 30 окон» стояло рядом с
                «0.99: 1009 окон», и выглядело сравнением цен. Старые
                одноступенчатые строки никуда не делись — их не удаляют, — и
                отличить их от лесенки иначе можно только запросом в базу. */}
            <Note
              label={
                <>
                  лесенка —{" "}
                  <span style={{ color: laddersTone(sources) }}>{laddersWord(sources)}</span>
                </>
              }
            >
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {sourcesOf(sources).map((src) => (
                  <li key={`ladder|${src}`}>
                    <b>лесенка · {sourceLabel(src)}</b> —{" "}
                    <span style={{ color: ladderTone(atSource(sources, src)) }}>
                      {ladderWord(atSource(sources, src))}
                    </span>
                    .
                  </li>
                ))}
              </ul>
            </Note>
            {/* Потолок объёма — отдельной строкой на источник, а не колонкой:
                ответ здесь не число, а РАЗЛИЧЕНИЕ «упёрлись» и «не упёрлись», и
                ноль в колонке прочитался бы как измеренный потолок. */}
            <Note label="ставка, потолок стакана и достаточность выборки — по ступеням">
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {sources.map((s) => {
                  const shape = shapeOf(shapes, `${s.limit_price}|${s.source}`);
                  return (
                    <li key={`${s.limit_price}|${s.source}`}>
                      <b>
                        {s.limit_price} · {sourceLabel(s.source)}
                      </b>{" "}
                      — <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                      {ceilingWord(s)}; {depthWord(s)};{" "}
                      <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.{" "}
                      {/* Сколько входов ступень обязана набрать, прежде чем её доля
                          угаданного что-то докажет. Без этого числа верхние ступени
                          лесенки выглядят в таблице лучше всех и не значат ничего:
                          «20/20» по 0.999 это запас в одну десятую процента, а по
                          0.95 — в пять, и по колонке «угадано» они неразличимы. */}
                      {provingWord(s)}.
                    </li>
                  );
                })}
              </ul>
            </Note>
            {/* Ворота согласия со стаканом — ОБЕ половины разреза, и обе вслух.
                Правило, чью цену не видно, снять нельзя: показав одну чистую
                половину, панель сделала бы ворота неопровержимыми. Знак счёта
                придержанных и есть приговор правилу. */}
            <Note
              label={
                <>
                  <span style={{ color: gatesTone(sources) }}>{gatesWord(sources)}</span> —
                  разбор по ступеням
                </>
              }
            >
              <ul className="space-y-1 text-xs text-[var(--color-muted)]">
                {sources.map((s) => (
                  <li key={`gate|${s.limit_price}|${s.source}`}>
                    <b>
                      ворота · {s.limit_price} · {sourceLabel(s.source)}
                    </b>{" "}
                    — <span style={{ color: gateTone(s) }}>{gateWord(s)}</span>; на свежем
                    стакане чистыми{" "}
                    <span className="num">{hitWord(s.clean_won, s.clean_settled)}</span> при
                    счёте <span className="num">{pnlWord(s.clean_pnl, s.clean_settled)}</span> на
                    обороте <span className="num">{formatDollars(s.clean_quote)}</span>.
                  </li>
                ))}
              </ul>
            </Note>
            {/* Мейкерская половина — ЦЕЛИКОМ и мелко. Вопрос по ней закрыт
                отрицательно (наливают единицы заявок из тысяч), но закрытый
                вопрос не стирают: без этих строк «мейкером не пробовали» и
                «мейкером пробовали, не налили» стали бы неразличимы. */}
            <Note label={`мейкерская половина: ${makerWord(sources)}`}>
              <ul className="space-y-1 pl-4">
                {sources.map((s) => (
                  <li key={`${s.limit_price}|${s.source}`}>
                    <b>
                      {s.limit_price} · {sourceLabel(s.source)}
                    </b>{" "}
                    — исполнимо {s.maker_feasible}, очередь впереди{" "}
                    {shares(s.queue_median)} share, {fillWord(s)}; счёт{" "}
                    {pnlWord(s.maker_pnl, s.maker_priced)}.
                  </li>
                ))}
              </ul>
            </Note>
            <Note label="как читать: угадано, кромка, лесенка и кто торгует">
              {modelGainWord(sources) ? <>{modelGainWord(sources)} </> : null}
              <b>Числа результата считаются по СВЕЖЕМУ стакану:</b> «угадано», «цена
              входа», «кромка», «запас», «оборот», «счёт», «доход» и гистограмма
              берут только входы по снимку моложе {freshBookMs} мс. На снимках
              старше цена взята из книги, которой на бирже уже нет, и там доля
              угаданного близка к единице по построению — 97 % прежнего теневого
              счёта лежало именно там. Сколько замера уцелело, говорит колонка
              «свежих» и подпись «стакан» выше.{" "}
              <b>«Угадано» считается по ВХОДАМ, а не по отобранным окнам:</b> вторая
              доля мерит замок (и она около единицы почти всегда), а решение
              принимают по первой — их знаменатели расходятся на порядок.{" "}
              <b>Кромка — это «угадано минус цена входа», и одна она ничего не
              доказывает:</b> запас объявляется в сигмах, потому что знак разности на
              такой выборке не значит ничего. Зелёное — от двух сигм и не раньше.{" "}
              <b>Счёт читается вместе с оборотом:</b> та же сумма на впятеро большем
              обороте — это уже не кромка, а везение.{" "}
              <b>Цена — СТУПЕНЬ ЛЕСЕНКИ, а не подпись строки:</b> по каждому
              отобранному окну пишется по строке на каждую ступень — с одного
              снимка стакана, в одну миллисекунду, с одной форой, — поэтому между
              строками остаётся ровно одна разница, и это цена. Читать лесенку
              надо РАЗНОСТЯМИ соседних ступеней: они вложены, и то, что берётся по
              0.95, берётся и по 0.99, так что лишний цент цены покупает разницу, а
              не отдельный опыт. <b>Проверять сопоставимость обязательно:</b> строка
              «лесенка» выше говорит, набраны ступени на одних окнах или на разных;
              на разных сравнивать их нельзя — это две даты, а не две цены.{" "}
              <b>Ордер ставится не по каждой строке:</b> торгует ОДНА клетка —
              источник и ступень цены, названные настройками, — а остальные строки
              окна остаются измерением. Кнопки выше и в ленте ничего не включают,
              пока торговать не разрешат ОБА сразу — потолок окружения и ключ
              оператора; на окно уходит не больше одного ордера, а суточный расход
              и число открытых заявок ограничены потолками стратегии.
            </Note>
          </>
        )}
      </Panel>

    </>
  );
}

/**
 * Лента входов — СВЁРНУТАЯ, и свёрнутая молча.
 *
 * Теневой она остаётся ровно до тех пор, пока режим ниже `alert`: с этой ступени
 * по строке торгующей клетки появляются кнопки, и вход перестаёт быть только
 * записью. Заголовок панели поэтому говорит про строки, а не про теневой счёт.
 *
 * Отдельным компонентом от сводки, чтобы страница могла поставить её НИЖЕ обеих
 * сводок: сводки сравнивают друг с другом — «почти решённое» против
 * «расхождения», — а не каждую со своей лентой.
 *
 * Свёрнута по умолчанию, потому что страница отвечает на вопрос «есть ли
 * кромка», а лента — на «что происходило»; второй задают, когда первый уже
 * получил ответ. Тело вынесено в отдельный компонент не ради порядка: под
 * FoldablePanel неразвёрнутое тело НЕ МОНТИРУЕТСЯ, и опрос раз в пятнадцать
 * секунд не уходит вовсе.
 *
 * С лесенкой одно окно даёт здесь по строке на КАЖДУЮ ступень, и повторы в
 * колонках отбора — не дубли: у строк общее всё, кроме цены и планов. Свести их
 * в одну значило бы спрятать ровно то, ради чего лента и открывается, — какой
 * ступени стакан ответил, а какой отказал в тот же миг.
 */
export function DecidedFeed({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel
      title="Входы"
      subtitle={`за ${hours} ч, по строке на источник И НА СТУПЕНЬ лесенки цен`}
    >
      <DecidedFeedBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

function DecidedFeedBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const feed = useDecidedSignals(hours, undefined, windowLen);
  return (
    <>
        {!feed.data ? (
          <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>
        ) : feed.data.signals.length === 0 ? (
          <Empty>входов за период не было</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--color-panel)]">
                <tr className="text-left text-[var(--color-muted)]">
                  <th className="pb-2 font-normal">когда</th>
                  <th className="pb-2 font-normal">источник</th>
                  <th className="pb-2 font-normal">рынок</th>
                  <th className="pb-2 font-normal">исход</th>
                  <th className="pb-2 text-right font-normal">фора</th>
                  <th className="pb-2 text-right font-normal">цена</th>
                  <th className="pb-2 font-normal">мейкер</th>
                  <th className="pb-2 font-normal">тейкер</th>
                  <th className="pb-2 text-right font-normal">налили</th>
                  <th className="pb-2 font-normal">что стало</th>
                  {/* Пустой заголовок: колонка появляется только на ступени
                      alert и выше, и подписывать её словом «действие» значило
                      бы обещать кнопку там, где её не будет. */}
                  <th className="pb-2 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {feed.data.signals.map((s) => (
                  <FeedRow key={s.id} signal={s} />
                ))}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
}

/**
 * Мейкерская половина одной фразой — для сводного заголовка свёрнутого блока.
 *
 * Считается по НАЛИТЫМ, а не по исполнимым: «исполнимо 1366» и «налито 16» —
 * разные утверждения, и первое без второго читается как тысяча сделок.
 */
function makerWord(sources: DecidedSource[]): string {
  const feasible = sources.reduce((a, s) => a + s.maker_feasible, 0);
  if (feasible === 0) return "заявку нельзя было поставить ни разу";
  const priced = sources.reduce((a, s) => a + s.maker_priced, 0);
  const pnl = sources.reduce((a, s) => a + Number(s.maker_pnl ?? 0), 0);
  if (priced === 0) return `исполнимо ${feasible}, считать пока нечем`;
  return `исполнимо ${feasible}, счёт ${formatDollars(String(pnl))} по ${priced} окнам`;
}

function SourceRow({
  source,
  shape,
  hours,
}: {
  source: DecidedSource;
  shape: ShadowShape | undefined;
  hours: number;
}) {
  // Ноль входов — РЕЗУЛЬТАТ замера, а не пустота, и красится он как
  // отрицательное число: серый прочерк читался бы как «ещё считаем».
  const takerTone = source.taker_feasible === 0 ? "neg" : "pos";
  const edge = takerEdge(source);
  const price = takerPrice(source);
  // Запас — по СВЕЖЕЙ половине, как и доля, против которой он считается: z по
  // всем входам и цена по свежим описывали бы разные выборки.
  const z = source.fresh_price_z;
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="num py-1.5">{source.limit_price}</td>
      <td className="py-1.5">
        <div>{sourceLabel(source.source)}</div>
        <div className="text-[11px] text-[var(--color-muted)]">
          {sourceHint(source.source)}
        </div>
      </td>
      {/* Входы СО ЗНАМЕНАТЕЛЕМ «из скольких окон»: без него шестьдесят входов
          читаются как шестьдесят возможностей, а их было семьсот. */}
      <td className={`num py-1.5 text-right ${toneColor(takerTone)}`}>
        {source.taker_feasible}
        <span className="text-[var(--color-muted)]">/{source.signals}</span>
      </td>
      {/* Сколько входов уцелело после среза по возрасту снимка стакана. Со
          знаменателем «из скольких сведённых»: без него «12» читается как
          двенадцать наблюдений, а их было сто двадцать пять. */}
      <td className="num py-1.5 text-right" style={{ color: staleTone(source) }}>
        {source.fresh_settled}
        <span className="text-[var(--color-muted)]">/{source.taker_settled}</span>
      </td>
      <td className="num py-1.5 text-right">
        {hitWord(source.fresh_won, source.fresh_settled)}
      </td>
      <td className="num py-1.5 text-right">
        {price === null ? "—" : price.toFixed(3)}
      </td>
      {/* Кромка НЕ красится сама по себе: цвет берётся у запаса в сигмах, иначе
          панель обещала бы кромку на разнице, неотличимой от случайной. */}
      <td className="num py-1.5 text-right">
        {edge === null ? "—" : pct(edge)}
      </td>
      <td className="num py-1.5 text-right" style={{ color: zTone(z) }}>
        {zWord(z)}
      </td>
      {/* «Взято» описывает СТАКАН, а не наш счёт: сколько share вообще лежало
          дешевле лимита. Величина про рынок, поэтому считается по всем строкам
          и срезом по возрасту снимка не режется. */}
      <td className="num py-1.5 text-right">{shares(source.taker_shares)}</td>
      <td className="num py-1.5 text-right">{formatDollars(source.fresh_quote)}</td>
      {/* Сумма ВМЕСТЕ со знаменателем: ноль в счёте бывает измеренный («вошли,
          не угадали») и пустой («считать было нечем»), и сумма их складывает.
          Прочерк при нулевом знаменателе — то же правило, что у копирования. */}
      <td className={`num py-1.5 text-right ${toneColor(tone(source.fresh_pnl))}`}>
        {pnlWord(source.fresh_pnl, source.fresh_priced)}
      </td>
      <td className={`num py-1.5 text-right ${toneColor(tone(source.fresh_pnl))}`}>
        {pct(takerRoi(source))}
      </td>
      {/* Удельный счёт — ВПРИТЫК к сумме и доходности, и отвечает он на третий
          вопрос: «счёт» это сколько, «доход» — КПД вложенного доллара, ставка —
          пропускная способность. Ступени лесенки заведены в разные часы, и без
          неё их суммы сравнивали бы возраст. */}
      <RateCell shape={shape} hours={hours} />
      {/* Форма СЧЁТА, а не ещё один его пересказ: сумма и доходность слева
          отвечают «сколько», гистограмма — «из чего». Плюс, собранный тремя
          сделками, и плюс, размазанный по всем, по колонке «счёт» неразличимы. */}
      <td className="py-1.5 text-right">
        <PnLShape shape={shape} />
      </td>
      <td className="num py-1.5 text-right">{lead(source.lead_median_ms)}</td>
    </tr>
  );
}

function FeedRow({ signal }: { signal: DecidedSignal }) {
  const when = new Date(signal.detected_at);
  return (
    <tr className="border-t border-[var(--color-border)] align-top">
      <td className="num py-1.5">{when.toISOString().slice(11, 19)}</td>
      <td className="py-1.5">{sourceLabel(signal.source)}</td>
      <td className="py-1.5">{marketWord(signal)}</td>
      <td className="py-1.5">{signal.outcome_name}</td>
      <td className="num py-1.5 text-right">{lead(signal.lead_ms)}</td>
      <td className="num py-1.5 text-right">{signal.limit_price}</td>
      <td className="py-1.5 text-[var(--color-muted)]">{planWord(signal.maker)}</td>
      <td className="py-1.5 text-[var(--color-muted)]">{planWord(signal.taker)}</td>
      <td className="num py-1.5 text-right">
        {/* Прочерк означает «не наблюдали», ноль — «никто не продал так дёшево».
            Разные ответы, и слить их значило бы потерять весь замер. */}
        {signal.maker_fill === undefined ? "—" : shares(signal.maker_fill)}
      </td>
      <td className="py-1.5">{outcomeWord(signal)}</td>
      <td className="py-1.5">
        <StrategyRowActions
          strategy="decided"
          signalID={signal.id}
          windowEnd={signal.window_end}
        />
      </td>
    </tr>
  );
}
